<template>
    <section
        :id="id"
        class="easy-steps"
        :class="basicClassName"
        v-bind="$attrs"
    >
        <div class="a-container">
            <h1 class="title">
                {{ title }}
            </h1>
            <p class="lead">
                {{ lead }}
            </p>
            <div class="steps">
                <div
                    v-for="(step, i) in steps"
                    :key="step"
                    class="step"
                    :class="{ active: i <= activeStep }"
                >
                    <div class="number">
                        {{ i + 1 }}
                    </div>
                    <div class="step-title">
                        {{ step }}
                    </div>
                </div>
                <div ref="progressBar" class="progress-bar" :style="progressBarStyle">
                    <div class="progress" />
                </div>
            </div>
            <p class="bottom-text">
                {{ bottomText }}
            </p>
        </div>
    </section>
</template>

<script>
import contentMixin from '@core/mixins/content.js';
import styleMixin from '@core/mixins/style.js';

export default {
    components: {

    },
    mixins: [contentMixin, styleMixin],
    props: {
        title: {
            type: String,
            default: '',
        },
        lead: {
            type: String,
            default: '',
        },
        steps: {
            type: Array,
            default: () => [],
        },
        bottomText: {
            type: String,
            default: '',
        },
        animationDuration: {
            type: Number,
            default: 1000,
        },
    },
    data: () => ({
        progress: 0,
        activeStep: -1,
    }),
    computed: {
        progressBarStyle() {
            return {
                '--duration': `${this.animationDuration}ms`,
                '--progress': `${this.progress}%`,
            };
        },
    },
    mounted() {
        window.addEventListener('resize', this.checkAnimationStart);
        window.addEventListener('scroll', this.checkAnimationStart);
        this.checkAnimationStart();
    },
    beforeDestroy() {
        window.removeEventListener('scroll', this.checkAnimationStart);
        window.removeEventListener('resize', this.checkAnimationStart);
    },
    methods: {
        startAnimation() {
            this.activeStep = 0;
            this.progress = 100;
            setTimeout(() => { this.activeStep += 1; }, this.animationDuration / 2);
            setTimeout(() => { this.activeStep += 1; }, this.animationDuration);
        },
        checkAnimationStart() {
            if (this.activeStep >= 0) return;

            const rect = this.$refs.progressBar.getBoundingClientRect();

            // Trigger when the component is visible in the viewport
            if (rect.top < window.innerHeight && rect.bottom > 0) {
                this.startAnimation();
                window.removeEventListener('scroll', this.checkAnimationStart);
                window.removeEventListener('resize', this.checkAnimationStart);
            }
        },
    },
};
</script>

<style lang="postcss" scoped>
.easy-steps {
    @mixin basic-slice-paddings;

    --progress: 0;
    --duration: 0;
    text-align: center;
    color: var(--av-nav-primary);
}

.title {
    @mixin display-accent;

    @media (--viewport-tablet) {
        @mixin hero-accent;
    }
}

.lead {
    @mixin title-accent;
    margin-top: 16px;

    @media (--viewport-tablet) {
        @mixin display-accent;
    }
}

.steps {
    position: relative;
    display: flex;
    flex-direction: column;
    row-gap: 72px;
    margin-block: 40px;
    padding: 24px 16px;
    border-radius: 24px;
    background: var(--av-brand-secondary-accent);
    column-gap: 0;

    @media (--viewport-tablet) {
        padding: 32px;
    }

    @media (--viewport-desktop) {
        padding: 56px 32px;
        flex-direction: row;
        justify-content: space-between;
    }
}

.progress-bar {
    display: block;
    position: absolute;
    top: 78px;
    bottom: 78px;
    inset-inline-start: 61px;
    width: 6px;
    background: var(--av-link-inversed);

    @media (--viewport-tablet) {
        inset-inline-start: 77px;
    }

    @media (--viewport-desktop) {
        top: 99px;
        width: auto;
        height: 6px;
        bottom: auto;
        inset-inline: calc(32px + 100% / 6);
    }
}

.progress {
    position: absolute;
    top: 0;
    width: 100%;
    inset-inline-start: 0;
    background: var(--av-nav-secondary);
    height: var(--progress);
    transition: height var(--duration) linear;

    @media (--viewport-desktop) {
        transition: width var(--duration) linear;
        height: 100%;
        width: var(--progress);
    }
}

.step {
    @mixin lead-accent;
    position: relative;
    z-index: 1;
    display: flex;
    flex-direction: row;
    gap: 22px;
    align-items: center;
    justify-content: flex-start;
    text-align: start;

    @media (--viewport-tablet) {
        @mixin title-accent;
    }

    @media (--viewport-desktop) {
        width: calc(100% / 3);
        flex-direction: column;
        text-align: center;
    }

    &.active {
        .number {
            background: var(--av-nav-secondary);
        }
    }
}

.number {
    @mixin hero-strong;
    color: var(--av-inversed-primary);
    display: flex;
    flex-shrink: 0;
    width: 96px;
    height: 92px;
    padding: 24px 24px 28px 24px;
    justify-content: center;
    align-items: center;
    border-radius: 24px;
    background: var(--av-link-inversed);
}

.bottom-text {
    @mixin lead;

    @media (--viewport-tablet) {
        @mixin title;
    }
}
</style>
